import * as React from "react";
import { useEffect } from "react";

// Third Party
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// UI Components
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
} from "@/components/ui/card";
import { ArrowLeft, FileUp, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { FileUpload } from "@/components/FileUpload";
import { Spinner } from "@/components/ui/spinner";
import { useToast } from "@/components/ui/use-toast";
import TableSkeleton from "@/components/skeleton/TableSkeleton";

// Services and Utils
import { getTaxDetails, editTax } from "@/services/tax-obligations";

// --- End Custom Number Input ---

export const Route = createFileRoute(
	"/_layout-private/report/tax-obligations/tax-liability-plan/edit",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const companyId = String(localStorage.getItem("companyId") || "");
	const yearly = Number(localStorage.getItem("yearly") || "");
	const taxId = localStorage.getItem("taxId") || "";
	const { toast } = useToast();

	// Local state with string values for each tax field.
	const [formData, setFormData] = React.useState({
		profitTax: "",
		addIncomeTax: "",
		monthlyTax: "",
		valueAddedTax: "",
		exciseTax: "",
		dividendTax: "",
		otherTaxes: "",
		docFile: "",
	});

	// Fetch tax details.
	const { data: taxDetailsData, isLoading } = useQuery({
		queryKey: ["tax-details", companyId, yearly],
		queryFn: () =>
			getTaxDetails({
				companyId,
				obligationType: "PLAN",
				yearly,
			}),
		enabled: !!companyId && !!yearly,
	});

	// Update the useEffect for initial data loading
	useEffect(() => {
		if (taxDetailsData?.data?.currentTaxPlan) {
			const plan = taxDetailsData.data.currentTaxPlan;
			setFormData({
				profitTax:
					plan.profitTax < 0
						? `(${Math.abs(plan.profitTax || 0)})`
						: String(plan.profitTax || 0),
				addIncomeTax:
					plan.addIncomeTax < 0
						? `(${Math.abs(plan.addIncomeTax || 0)})`
						: String(plan.addIncomeTax || 0),
				monthlyTax:
					plan.monthlyTax < 0
						? `(${Math.abs(plan.monthlyTax || 0)})`
						: String(plan.monthlyTax || 0),
				valueAddedTax:
					plan.valueAddedTax < 0
						? `(${Math.abs(plan.valueAddedTax || 0)})`
						: String(plan.valueAddedTax || 0),
				exciseTax:
					plan.exciseTax < 0
						? `(${Math.abs(plan.exciseTax || 0)})`
						: String(plan.exciseTax || 0),
				dividendTax:
					plan.dividendTax < 0
						? `(${Math.abs(plan.dividendTax || 0)})`
						: String(plan.dividendTax || 0),
				otherTaxes:
					plan.otherTaxes < 0
						? `(${Math.abs(plan.otherTaxes || 0)})`
						: String(plan.otherTaxes || 0),
				docFile: taxDetailsData.data.docFile || "",
			});
		}
	}, [taxDetailsData]);

	// Mutation for editing tax details.
	const mutation = useMutation({
		mutationFn: (data: any) => editTax(taxDetailsData?.data?.id, data),
		onSuccess: () => {
			toast({
				title: "ເພີ່ມຂໍ້ມູນສຳເລັດ",
				description: "ເພີ່ມແຜນມອບພັນທະໃຫ້ລັດ",
				variant: "success",
			});
			queryClient.invalidateQueries({
				queryKey: ["tax-details", companyId, yearly],
			});
			navigate({ to: "/report/tax-obligations" });
		},
		onError: (error: any) => {
			console.error("Error editing tax details:", error);

			toast({
				title: "ເກີດຂໍ້ຜິດພາດ",
				description: "ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	function formatNumberWithCommas(value: string | number) {
		const stringValue = String(value || "");
		const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
		const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

		// Split number into integer and decimal parts
		const [integerPart, decimalPart] = numericValue.split(".");

		// Format integer part with commas
		const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

		// Combine with decimal part if it exists
		const formattedValue =
			decimalPart !== undefined
				? `${formattedInteger}.${decimalPart}`
				: formattedInteger;

		return isNegative ? `(${formattedValue})` : formattedValue;
	}

	function parseParenthesisNumber(strValue: string | number) {
		if (!strValue) return 0;
		const stringValue = String(strValue).replace(/,/g, "");
		const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
		const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;
		return isNegative ? -Number(numericValue) : Number(numericValue);
	}

	// Update a field in the state.
	const handleInputChange = (name: string, value: string) => {
		// For tax amount fields
		let processedValue = value.replace(/,/g, "");

		// Check if input is valid
		if (!/^[0-9().]*$/.test(processedValue)) {
			return;
		}

		// Handle parentheses
		if (processedValue.startsWith("(")) {
			// Remove all existing closing parentheses
			processedValue = processedValue.replace(/\)/g, "");
			// Add single closing parenthesis
			processedValue = processedValue + ")";
		} else {
			// Remove any stray parentheses if not starting with '('
			processedValue = processedValue.replace(/[()]/g, "");
		}

		// Handle decimal points
		if (processedValue.includes(".")) {
			const [integerPart, decimalPart] = processedValue.split(".");
			if (decimalPart && decimalPart.length > 2) {
				const cleanDecimal = decimalPart.replace(/\)/g, "");
				processedValue = `${integerPart}.${cleanDecimal.slice(0, 2)}${processedValue.endsWith(")") ? ")" : ""}`;
			}
		}

		setFormData((prev) => ({ ...prev, [name]: processedValue }));
	};

	// Handle file upload update.
	const handleFileUpload = (file: string) => {
		setFormData((prev) => ({ ...prev, docFile: file }));
	};

	// Compute the total tax.
	const taxFields = [
		"profitTax",
		"addIncomeTax",
		"monthlyTax",
		"valueAddedTax",
		"exciseTax",
		"dividendTax",
		"otherTaxes",
	];

	const totalTax = taxFields.reduce((sum, field) => {
		const value = Number(formData[field].replace(/,/g, "")) || 0;
		return sum + value;
	}, 0);
	const formattedTotalTax = new Intl.NumberFormat().format(totalTax);

	// Handle form submission.
	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!formData.docFile) {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ກະລຸນາອັບໂຫຼດເອກະສານກ່ອນບັນທຶກ",
				variant: "destructive",
			});

			return;
		}

		const submitData = {
			companyId,
			taxId,
			obligationType: "PLAN",
			yearly,
			profitTax: parseParenthesisNumber(formData.profitTax),
			addIncomeTax: parseParenthesisNumber(formData.addIncomeTax),
			monthlyTax: parseParenthesisNumber(formData.monthlyTax),
			valueAddedTax: parseParenthesisNumber(formData.valueAddedTax),
			exciseTax: parseParenthesisNumber(formData.exciseTax),
			dividendTax: parseParenthesisNumber(formData.dividendTax),
			otherTaxes: parseParenthesisNumber(formData.otherTaxes),
			docFile: formData.docFile,
		};
		mutation.mutate(submitData);
	};

	if (isLoading) return <TableSkeleton />;
	if (!taxDetailsData?.data) return <div>No data found</div>;

	return (
		<section>
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbLink>
							<Link
								replace
								to="/report/tax-obligations?active=taxLiabilityPlan"
							>
								ລາຍງານແຜນມອບພັນທະອາກອນ
							</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbLink>
							<Link replace to="/report/tax-obligations/tax-liability-plan">
								ລາຍງານພັນທະອາກອນ
							</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>ແກ້ໄຂລາຍງານພັນທະອາກອນ</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			<Card className="mt-8">
				<CardHeader>
					<button
						type="button"
						onClick={() =>
							navigate({ to: "/report/tax-obligations/tax-liability-plan" })
						}
						className="flex items-center gap-1 mb-4"
						aria-label="Go back to tax obligations report"
					>
						<ArrowLeft className="w-5 h-5" />
						ກັບຄືນ
					</button>
					<CardTitle>
						ລາຍລະອຽດເອກະສານມອບພັນທະໃຫ້ລັດ ງວດປີ {taxDetailsData.data.yearly}
					</CardTitle>
					<CardDescription>ລາຍລະອຽດ</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit}>
						<div>
							<Table className="w-full mb-4">
								<TableHeader>
									<TableRow className="[&>*]:font-bold [&>*]:text-black [&>*]:bg-gray-100">
										<TableHead className="border">ເນື້ອໃນ</TableHead>
										<TableHead className="border">
											ແຜນມອບເງິນ {taxDetailsData.data.yearly}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									<TableRow className="cursor-pointer">
										<TableCell className="font-medium border">
											ອາກອນກຳໄລ
										</TableCell>
										<TableCell className="font-medium border">
											<Input
												type="text"
												name="profitTax"
												value={formatNumberWithCommas(formData.profitTax)}
												onChange={(e) =>
													handleInputChange("profitTax", e.target.value)
												}
												placeholder="ປ້ອນ..."
												className="text-right"
												inputMode="numeric"
											/>
										</TableCell>
									</TableRow>
									<TableRow className="cursor-pointer">
										<TableCell className="font-medium border">
											ອາກອນກຳໄລມອບເກີນ ຫຼື ມອບເພີ່ມ
										</TableCell>
										<TableCell className="font-medium border">
											<Input
												type="text"
												name="addIncomeTax"
												value={formatNumberWithCommas(formData.addIncomeTax)}
												onChange={(e) =>
													handleInputChange("addIncomeTax", e.target.value)
												}
												placeholder="ປ້ອນ..."
												className="text-right"
												inputMode="numeric"
											/>
										</TableCell>
									</TableRow>
									<TableRow className="cursor-pointer">
										<TableCell className="font-medium border">
											ອາກອນເງິນເດືອນ
										</TableCell>
										<TableCell className="font-medium border">
											{/* <NumberInput
												name="monthlyTax"
												value={formData.monthlyTax}
												onChange={handleInputChange}
												placeholder="ປ້ອນ"
											/> */}
											<Input
												type="text"
												name="monthlyTax"
												value={formatNumberWithCommas(formData.monthlyTax)}
												onChange={(e) =>
													handleInputChange("monthlyTax", e.target.value)
												}
												placeholder="ປ້ອນ..."
												className="text-right"
												inputMode="numeric"
											/>
										</TableCell>
									</TableRow>
									<TableRow className="cursor-pointer">
										<TableCell className="font-medium border">
											ອາກອນມູນຄ່າເພີ່ມ (ອພມ)
										</TableCell>
										<TableCell className="font-medium border">
											{/* <NumberInput
												name="valueAddedTax"
												value={formData.valueAddedTax}
												onChange={handleInputChange}
												placeholder="ປ້ອນ"
											/> */}
											<Input
												type="text"
												name="valueAddedTax"
												value={formatNumberWithCommas(formData.valueAddedTax)}
												onChange={(e) =>
													handleInputChange("valueAddedTax", e.target.value)
												}
												placeholder="ປ້ອນ..."
												className="text-right"
												inputMode="numeric"
											/>
										</TableCell>
									</TableRow>
									<TableRow className="cursor-pointer">
										<TableCell className="font-medium border">
											ອາກອນຊົມໃຊ້
										</TableCell>
										<TableCell className="font-medium border">
											{/* <NumberInput
												name="exciseTax"
												value={formData.exciseTax}
												onChange={handleInputChange}
												placeholder="ປ້ອນ"
											/> */}

											<Input
												type="text"
												name="exciseTax"
												value={formatNumberWithCommas(formData.exciseTax)}
												onChange={(e) =>
													handleInputChange("exciseTax", e.target.value)
												}
												placeholder="ປ້ອນ..."
												className="text-right"
												inputMode="numeric"
											/>
										</TableCell>
									</TableRow>
									<TableRow className="cursor-pointer">
										<TableCell className="font-medium border">
											ອາກອນເງິນປັນຜົນ
										</TableCell>
										<TableCell className="font-medium border">
											{/* <NumberInput
												name="dividendTax"
												value={formData.dividendTax}
												onChange={handleInputChange}
												placeholder="ປ້ອນ"
											/> */}

											<Input
												type="text"
												name="dividendTax"
												value={formatNumberWithCommas(formData.dividendTax)}
												onChange={(e) =>
													handleInputChange("dividendTax", e.target.value)
												}
												placeholder="ປ້ອນ..."
												className="text-right"
												inputMode="numeric"
											/>
										</TableCell>
									</TableRow>
									<TableRow className="cursor-pointer">
										<TableCell className="font-medium border">
											ອາກອນອື່ນໆ
										</TableCell>
										<TableCell className="font-medium border">
											{/* <NumberInput
												name="otherTaxes"
												value={formData.otherTaxes}
												onChange={handleInputChange}
												placeholder="ປ້ອນ"
											/> */}

											<Input
												type="text"
												name="otherTaxes"
												value={formatNumberWithCommas(formData.otherTaxes)}
												onChange={(e) =>
													handleInputChange("otherTaxes", e.target.value)
												}
												placeholder="ປ້ອນ..."
												className="text-right"
												inputMode="numeric"
											/>
										</TableCell>
									</TableRow>
									<TableRow className="bg-[#E4E4E7] [&>*]:font-bold [&>*]:text-black">
										<TableCell>ລວມມອບອາກອນໃນປີ</TableCell>
										<TableCell className="text-right">
											{formattedTotalTax}
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</div>

						<div className="my-4 w-fit">
							<FileUpload
								currentFile={formData.docFile}
								onFileUpload={handleFileUpload}
							/>
						</div>

						<div className="flex items-center justify-end w-full gap-2">
							<Button
								type="button"
								variant={"outline"}
								onClick={() =>
									navigate({
										to: "/report/tax-obligations/tax-liability-plan",
									})
								}
							>
								<span>ຍົກເລີກ</span>
							</Button>
							<Button
								disabled={mutation.isPending}
								className="flex items-center gap-2"
								type="submit"
							>
								{mutation.isPending ? (
									<Spinner className="text-white" />
								) : (
									<>
										<Save className="mr-2" /> ບັນທຶກ
									</>
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</section>
	);
}

export default RouteComponent;
